import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Shield, Award, Clock, CheckCircle, MapPin, ChevronRight, Star, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

function setMeta(name, content, property) {
  const attr = property ? "property" : "name";
  const val = property || name;
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function SEOLandingTemplate({
  slug,
  primaryKeyword,
  h1Title,
  h1Highlight,
  metaTitle,
  metaDescription,
  heroImage,
  introParagraph,
  services,
  faqs,
  areaName,
  areaDescription,
  relatedPages,
  schemaService,
}) {
  useEffect(() => {
    document.title = metaTitle;
    setMeta("description", metaDescription);
    setMeta("keywords", primaryKeyword);
    setMeta(null, metaTitle, "og:title");
    setMeta(null, metaDescription, "og:description");
    setMeta(null, `https://paradisevalleytreeservice.com/${slug}`, "og:url");
    setMeta(null, "website", "og:type");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://paradisevalleytreeservice.com/${slug}`);

    const schemaLD = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": schemaService || primaryKeyword,
      "provider": {
        "@id": "https://paradisevalleytreeservice.com/#organization"
      },
      "areaServed": { "@type": "City", "name": areaName?.split(",")[0]?.trim() || "Scottsdale" },
      "description": metaDescription,
      "url": `https://paradisevalleytreeservice.com/${slug}`
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://paradisevalleytreeservice.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": (schemaService || primaryKeyword).replace(/\b\w/g, l => l.toUpperCase()),
          "item": `https://paradisevalleytreeservice.com/${slug}`
        }
      ]
    };

    const faqSchema = faqs?.length ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    } : null;

    const serviceScript = document.createElement("script");
    serviceScript.type = "application/ld+json";
    serviceScript.textContent = JSON.stringify(schemaLD);
    serviceScript.setAttribute("data-seo-landing", slug);
    document.head.appendChild(serviceScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    breadcrumbScript.setAttribute("data-seo-breadcrumb", slug);
    document.head.appendChild(breadcrumbScript);

    let faqScript;
    if (faqSchema) {
      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify(faqSchema);
      faqScript.setAttribute("data-seo-landing-faq", slug);
      document.head.appendChild(faqScript);
    }

    return () => {
      serviceScript.remove();
      breadcrumbScript.remove();
      if (faqScript) faqScript.remove();
    };
  }, [slug, primaryKeyword, metaTitle, metaDescription, schemaService, areaName, faqs]);

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative min-h-[65vh] flex items-center" data-testid={`landing-hero-${slug}`}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${heroImage}')` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span className="text-[#10B981] text-sm font-bold tracking-wider uppercase">ISA Certified Arborists</span>
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {h1Title} <span className="text-[#10B981]">{h1Highlight}</span>
              </h1>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">{introParagraph}</p>
              <div className="flex flex-wrap gap-4">
                <Button asChild className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-5 uppercase tracking-wider text-sm font-bold shadow-lg">
                  <a href="tel:4803517775"><Phone className="w-4 h-4 mr-2" /> Call (480) 351-7775</a>
                </Button>
                <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-8 py-5 uppercase tracking-wider text-sm font-bold">
                  <a href="/#contact">Get Free Estimate</a>
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-white/60 text-sm">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#D4A853]" fill="#D4A853" /> 5-Star Rated</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> 24/7 Emergency</span>
                <span className="flex items-center gap-1"><Award className="w-4 h-4" /> Since 2015</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <div className="bg-[#0A1628] py-4 border-t border-[#10B981]/30">
          <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-8 text-white/80 text-sm">
            {["ISA Certified Arborists", "Fully Licensed & Insured", "$5M Coverage", "1,500+ Properties Served", "0% Financing Available"].map((t, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10B981]" />{t}</span>
            ))}
          </div>
        </div>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-3">Our Services</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#0A1628]">
                Professional {primaryKeyword.split(" ").slice(0, 3).join(" ").replace(/\b\w/g, l => l.toUpperCase())} Services
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((svc, i) => (
                <div key={i} className="bg-[#F8F6F1] p-8 border-t-4 border-[#10B981] hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-[#10B981]/10 flex items-center justify-center mb-4">
                    {svc.icon === "flame" ? <Flame className="w-6 h-6 text-[#10B981]" /> : <CheckCircle className="w-6 h-6 text-[#10B981]" />}
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#0A1628] mb-3">{svc.title}</h3>
                  <p className="text-[#64748B] text-sm leading-relaxed mb-4">{svc.description}</p>
                  {svc.price && <p className="text-[#10B981] font-bold text-sm">Starting at {svc.price}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us + Area Info */}
        <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">Why Choose Us</p>
                <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-white mb-8">
                  {areaName}'s Most Trusted Tree Care Experts
                </h2>
                {[
                  "Every job supervised by ISA Certified Arborist",
                  "Specialized in Arizona native trees: Palo Verde, Mesquite, Palm, Citrus",
                  "Transparent pricing with written estimates - no hidden fees",
                  "24/7 emergency response during monsoon season",
                  "Fully licensed, insured, and bonded - $5M coverage",
                  "Locally owned and operated since 2015",
                  "1,500+ luxury properties maintained across Arizona"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 mb-4">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">Service Area</p>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-4">Serving {areaName}</h3>
                <p className="text-white/70 mb-8 leading-relaxed">{areaDescription}</p>
                <div className="bg-white/5 border border-white/10 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="w-5 h-5 text-[#D4A853]" />
                    <span className="text-white font-bold">Local Service Areas</span>
                  </div>
                  {["Paradise Valley", "Scottsdale", "Phoenix", "Fountain Hills", "Cave Creek", "Carefree", "Mesa"].map((area, i) => (
                    <span key={i} className="inline-block bg-[#10B981]/20 text-[#10B981] text-sm px-3 py-1 mr-2 mb-2">{area}</span>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-gradient-to-r from-[#DC2626]/20 to-transparent border-l-4 border-[#DC2626]">
                  <p className="text-white font-bold mb-1">24/7 Emergency Service</p>
                  <p className="text-white/60 text-sm">Storm damage? Fallen tree? Call us anytime at <a href="tel:4803517775" className="text-[#10B981] font-bold">(480) 351-7775</a></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        {faqs?.length > 0 && (
          <section className="py-20 bg-[#F8F6F1]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-3">Common Questions</p>
                <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#0A1628]">
                  Frequently Asked Questions
                </h2>
              </div>
              <div className="space-y-6">
                {faqs.map((faq, i) => (
                  <div key={i} className="bg-white p-6 shadow-sm border-l-4 border-[#10B981]">
                    <h3 className="font-bold text-[#0A1628] mb-2">{faq.q}</h3>
                    <p className="text-[#64748B] text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-[#10B981] to-[#059669]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready for Professional Tree Care?
            </h2>
            <p className="text-white/90 text-lg mb-8">Call now for a free estimate. Same-day consultations available for {areaName} properties.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="bg-white text-[#0A1628] hover:bg-[#F8F6F1] rounded-none px-10 py-5 uppercase tracking-wider text-sm font-bold shadow-lg">
                <a href="tel:4803517775"><Phone className="w-4 h-4 mr-2" /> (480) 351-7775</a>
              </Button>
              <Button asChild className="bg-[#0A1628] text-white hover:bg-[#1E3A5F] rounded-none px-10 py-5 uppercase tracking-wider text-sm font-bold">
                <a href="/#contact">Request Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Pages */}
        {relatedPages?.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628] mb-8 text-center">Related Services</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedPages.map((rp, i) => (
                  <Link key={i} to={rp.href} className="flex items-center gap-2 p-4 bg-[#F8F6F1] hover:bg-[#10B981]/10 border-l-4 border-[#10B981] transition-colors">
                    <ChevronRight className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                    <span className="text-[#0A1628] text-sm font-medium">{rp.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}
